import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ThemeState {
  theme: 'light' | 'dark';
  toggleTheme: (origin?: { x: number; y: number }) => void;
  setTheme: (theme: 'light' | 'dark') => void;
  initializeTheme: () => void;
}

function applyThemeToDocument(theme: 'light' | 'dark') {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

function animateThemeRipple(theme: 'light' | 'dark', origin?: { x: number; y: number }): boolean {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const docWithTransition = document as any;
  const startViewTransition = docWithTransition.startViewTransition as
    | ((callback: () => void) => { ready: Promise<void> })
    | undefined;

  if (!startViewTransition || prefersReducedMotion) {
    return false;
  }

  const x = Number.isFinite(origin?.x) ? Number(origin?.x) : window.innerWidth / 2;
  const y = Number.isFinite(origin?.y) ? Number(origin?.y) : window.innerHeight / 2;
  const endRadius = Math.hypot(
    Math.max(x, window.innerWidth - x),
    Math.max(y, window.innerHeight - y),
  );

  let transition: { ready: Promise<void> };
  try {
    transition = docWithTransition.startViewTransition(() => {
      applyThemeToDocument(theme);
    });
  } catch {
    return false;
  }

  transition.ready
    .then(() => {
      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${endRadius}px at ${x}px ${y}px)`,
          ],
        },
        {
          duration: 520,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          pseudoElement: '::view-transition-new(root)',
        } as KeyframeAnimationOptions,
      );
    })
    .catch(() => undefined);

  return true;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      
      toggleTheme: (origin?: { x: number; y: number }) => {
        const currentTheme = get().theme;
        const newTheme = currentTheme === 'light' ? 'dark' : 'light';
        set({ theme: newTheme });
        const animated = animateThemeRipple(newTheme, origin);
        if (!animated) {
          applyThemeToDocument(newTheme);
        }
      },
      
      setTheme: (theme: 'light' | 'dark') => {
        set({ theme });
        applyThemeToDocument(theme);
      },
      
      initializeTheme: () => {
        const { theme } = get();
        applyThemeToDocument(theme);
      },
    }),
    {
      name: 'theme-storage',
      version: 1,
    }
  )
);
